import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  Grid,
} from '@mui/material';
import {
  SelfServeUpdateCategoryAndGenrePayload,
  useLazyFetchSelfServeMMPConfigQuery,
  useSelfServeDataAccessMutation,
  useUpdateCategoryAndGenreMutation,
} from '@api/selfServe';
import {
  APPLICATION_STORES,
  DataAccessProvider,
  SelfServeDataAccessStatus,
  TOAST_TYPES,
} from '@constants/constants';
import { trackGAEvent } from '@utils/analytics';
import { CustomAccordion } from '../../../../../components';
import DrawerComponent from '../../../../../components/Drawer/Drawer';
import GrantAccessToProvider from '../DataAccess/GrantAccessToProvider';
import ProcessingDataAccess from '../DataAccess/ProcessingDataAccess';
import EditGame from '../GameData/EditGame';
import GameDetails, { IGameDetails } from '../GameData/GameDetails';
import { setToastMessage } from '@stores/App/slice';
import { processRatingString } from '@utils/common';
import DataAccessCardHeader from '../DataAccess/DataAccessCardHeader';
import { ProviderConfig } from '../../constants';
import AppsFlyerInformation from '../DataAccess/AppsFlyerProvider/AppsFlyerInformation';
import AdjustInformation from '../DataAccess/AdjustProvider/AdjustInformation';
import { useLazyGetCategoriesAndGenresQuery } from '@api/games';
import DataRequirementDocumentChip from '../DataRequirementDocumentChip';
import DataProcessingErrorCard from '../DataProcessingErrorCard';
import { PendingAccessCardProps } from '../../types';

interface FetchedConfig {
  APPS_FLYER: {
    iap: any;
    iaa: any;
  };
  ADJUST: {
    iap: any;
    iaa: any;
  };
}

const PendingAccessCard: FC<PendingAccessCardProps> = ({ data, fetchApplicationDetails }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();

  const [refreshStatus, setRefreshStatus] = useState<boolean>(data.monthlyRefreshStatus || true);
  const [applicationDetailsResponse, setApplicationDetailsResponse] = useState<{
    category: string | null;
    genre: string | null;
    selfServeId: string;
    requestStatus: string;
    accessEmail: string;
    mmp: string;
  } | null>(null);
  const [accordionConfig, setAccordionConfig] = useState<
    { isSuccess: boolean; open: boolean }[] | null
  >(null);
  const [playStoreApp, setPlayStoreApp] = useState<null | IGameDetails>(null);
  const [appStoreApp, setAppStoreApp] = useState<null | IGameDetails>(null);
  const [category, setCategory] = useState<string>('');
  const [genre, setGenre] = useState<string>('');
  const [categoryOptions, setCategoryOptions] = useState<{ name: string; genres: string[] }[]>([]);
  const [genreOptions, setGenreOptions] = useState<string[]>([]);
  const [isEditGameOpen, setIsEditGameOpen] = useState<boolean>(false);
  const [isOpenGrantAccessPanel, setOpenGrantAccessPanel] = useState<boolean>(false);
  const [accessGrantScrollTo, setAccessGrantScrollTo] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<DataAccessProvider>(
    DataAccessProvider.APPS_FLYER,
  );
  const [activeConfig, setActiveConfig] = useState<ProviderConfig | null>(null);
  const [mmpConfig, setMmpconfig] = useState<FetchedConfig | null>(null);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const [fetchCategoryAndGenreOptionsApi] = useLazyGetCategoriesAndGenresQuery();
  const [updateCategoryAndGenreApi] = useUpdateCategoryAndGenreMutation();
  const [selfServeDataAccessApi] = useSelfServeDataAccessMutation();
  const [isDataAccessUpdating, setIsDataAccessUpdating] = useState<boolean>(false);
  const [fetchSelfServeMMPConfigQuery] = useLazyFetchSelfServeMMPConfigQuery();

  const processingDataAccessStatus = new Set<string>([
    SelfServeDataAccessStatus.ACCESS_GRANTED,
    SelfServeDataAccessStatus.DATA_FETCH_SUCCESSFUL,
    SelfServeDataAccessStatus.DATA_FETCH_PARTIAL,
  ]);

  const pendingDataAccessStatus = new Set<string>([
    SelfServeDataAccessStatus.PENDING_ACCESS,
    SelfServeDataAccessStatus.CALL_SCHEDULED,
  ]);
  const errorDataAccessStatus = new Set<string>([
    SelfServeDataAccessStatus.DATA_FETCH_FAILED,
    SelfServeDataAccessStatus.DASHBOARD_CREATION_FAILED,
  ]);

  useEffect(() => {
    setApplicationDetailsResponse({
      selfServeId: data.id,
      category: data.category,
      genre: data.genre,
      requestStatus: data.requestStatus,
      accessEmail: data.accessEmail,
      mmp: data.mmp,
    });

    setSelectedProvider(data.mmp as DataAccessProvider);
    updateAccordionConfig(data.category, data.genre, data.requestStatus);
    updateApplicationDetails(data.metadata);
  }, [data]);

  // Hook for fetching the category and genre options.
  useEffect(() => {
    fetchCategoryAndGenreOptions();
    fetchDataAccessProviderConfigs();
  }, []);

  // Hook for updating genre and genre options when category is changed.
  useEffect(() => {
    setGenre('');
    if (category === null) return;

    let genres: string[] = [];
    for (const cat of categoryOptions) {
      if (cat.name !== category) continue;

      genres = cat.genres;
      break;
    }

    setGenreOptions(genres);
  }, [category]);

  useEffect(() => {
    return () => {
      stopPolling(); // Cleanup on unmount
    };
  }, []);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = () => {
    if (pollingRef.current) return; // Prevent multiple intervals

    pollingRef.current = setInterval(() => {
      fetchApplicationDetails();
    }, 60000);
  };

  const fetchDataAccessProviderConfigs = async () => {
    try {
      const { data } = await fetchSelfServeMMPConfigQuery().unwrap();
      let config: FetchedConfig = {
        APPS_FLYER: {
          iap: {},
          iaa: [],
        },
        ADJUST: {
          iap: [],
          iaa: [],
        },
      };

      if (data?.APPS_FLYER?.iapInformation) {
        config.APPS_FLYER.iap =
          data.APPS_FLYER.iapInformation.map((item: any) => ({
            label: `${(Number(item.value) * 100).toFixed(0)}%`,
            value: item.value,
          })) || [];
      }
      if (data?.APPS_FLYER?.iaaInformation) {
        config.APPS_FLYER.iaa =
          data.APPS_FLYER.iaaInformation.map((item: any) => ({
            label: item.name,
            value: item.id,
          })) || [];
      }
      if (data?.ADJUST?.iapInformation) {
        config.ADJUST.iap =
          data.ADJUST.iapInformation.map((item: any) => ({
            label: `${(Number(item.value) * 100).toFixed(0)}%`,
            value: item.value,
          })) || [];
      }
      if (data?.ADJUST?.iaaInformation) {
        config.ADJUST.iaa =
          data.ADJUST.iaaInformation.map((item: any) => ({
            label: item.name,
            value: item.id,
          })) || [];
      }

      setMmpconfig(config);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || null,
        }),
      );
    }
  };

  const updateApplicationDetails = (metadata: string) => {
    const parsedMetadata = JSON.parse(metadata);

    const playStoreApp: IGameDetails = parsedMetadata[APPLICATION_STORES.PLAY_STORE] || null;
    const appStoreApp: IGameDetails = parsedMetadata[APPLICATION_STORES.APP_STORE] || null;

    if (playStoreApp?.rating) {
      playStoreApp['rating'] = processRatingString(playStoreApp['rating'] as string);
    }

    if (appStoreApp?.rating) {
      appStoreApp['rating'] = processRatingString(appStoreApp['rating'] as string);
    }

    setPlayStoreApp(playStoreApp);
    setAppStoreApp(appStoreApp);
  };

  const updateAccordionConfig = (
    category: string | null,
    genre: string | null,
    requestStatus: string,
  ) => {
    const accordionConfig = [
      {
        isSuccess: false,
        open: false,
      },
    ];

    if (category !== null && genre !== null) {
      if (
        pendingDataAccessStatus.has(requestStatus) ||
        processingDataAccessStatus.has(requestStatus)
      ) {
        accordionConfig[0].open = true;
      }
    }

    setAccordionConfig(accordionConfig);
  };

  const fetchCategoryAndGenreOptions = async (): Promise<void> => {
    try {
      const response = await fetchCategoryAndGenreOptionsApi().unwrap();
      const { data } = response;

      setCategoryOptions(data.categories);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || null,
        }),
      );
    }
  };

  const handleSubmitCategoryAndGenre = async (): Promise<void> => {
    // Validations done for submitting category and genre submit.
    if (category === null || genre === null) {
      return;
    }

    if (!!!applicationDetailsResponse || !applicationDetailsResponse.selfServeId) {
      return;
    }

    const payload: SelfServeUpdateCategoryAndGenrePayload = {
      category,
      genre,
    };
    const selfServeId = applicationDetailsResponse.selfServeId as string;

    trackGAEvent({
      action: 'set_category_genre',
      category: 'onboarding',
    });

    try {
      await updateCategoryAndGenreApi({
        selfServeId,
        payload,
      }).unwrap();

      fetchApplicationDetails();
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || null,
        }),
      );
    }
  };

  const handleEditGameClose = (cancel: boolean = false) => {
    if (!cancel) fetchApplicationDetails();

    setIsEditGameOpen(false);
  };

  const handleEditGameOpen = () => {
    setIsEditGameOpen(true);
  };

  const handleScrollTo = (sidePanelId: string) => {
    setAccessGrantScrollTo(sidePanelId);
  };

  const handleGrantAccessOpen = () => {
    const selectedConfig = DATA_ACCESS_PROVIDERS.find(
      (provider) => provider.value === selectedProvider,
    );

    if (!selectedConfig) return;

    setActiveConfig(selectedConfig);
    setAccessGrantScrollTo('');
    setOpenGrantAccessPanel(true);
  };

  const handleGrantAccessSave = async (formData: Record<string, string | string[]>) => {
    if (Object.keys(formData).length > 0) {
      // if the dialog closed with data
      setIsDataAccessUpdating(true);
      if (activeConfig && activeConfig.value) {
        await handleConfirmAccessClick(formData, activeConfig.value);
      }
      setIsDataAccessUpdating(false);
    } else if (isDataAccessUpdating) {
      // if the dialog closed without data - cancel
      return;
    }

    setAccessGrantScrollTo('');
    setOpenGrantAccessPanel(false);
    setActiveConfig(null);
  };

  const handleGrantAccessContinue = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    handleGrantAccessOpen();
  };

  const getDataAccessMethod = (mmp: DataAccessProvider): string => {
    switch (mmp) {
      case DataAccessProvider.APPS_FLYER:
        return 'appsFlyer';
      case DataAccessProvider.ADJUST:
        return 'adjust';
      case DataAccessProvider.BIGQUERY:
        return 'bigQuery';
      case DataAccessProvider.GCS:
        return 'gcs';
      case DataAccessProvider.S3:
        return 's3';
      case DataAccessProvider.MYSQL:
        return 'mysql';
      case DataAccessProvider.SNOWFLAKE:
        return 'snowflake';
      case DataAccessProvider.GOOGLE_DRIVE:
        return 'googleDrive';
      case DataAccessProvider.AGGREGATED_DATA:
        return 'aggregatedCohortData';
      default:
        return 'unknown';
    }
  };

  const handleConfirmAccessClick = async (payload: any, mmp: DataAccessProvider) => {
    trackGAEvent({
      action: 'confirm_data_access',
      category: 'onboarding',
      method: getDataAccessMethod(mmp),
    });

    try {
      await selfServeDataAccessApi({
        selfServeId: applicationDetailsResponse?.selfServeId as string,
        accountName: payload?.accountName || null,
        mmp,
        iapMargin: payload?.iapMargin || null,
        ...(payload.iaaIdList ? { iaaIdList: payload.iaaIdList } : {}),
        ...(payload.connectionDetails ? { connectionDetails: payload.connectionDetails } : {}),
        refreshRequired: mmp === DataAccessProvider.AGGREGATED_DATA ? false : refreshStatus,
      }).unwrap();
      startPolling();
    } catch (error: any) {
      const { data, detailedErrorObject } = error;

      if (detailedErrorObject?.code === 4052) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            heading: 'Adjust Account Name Not Found!',
            message:
              "Please ensure that access was granted to underwriting@pvxpartner.com and that your account name exactly matches your Adjust account. If you've already done this, please try syncing again or reach us at info@pvxpartners.com.",
          }),
        );
      } else if (detailedErrorObject?.code === 4053) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            heading: 'Game IDs Not Found!',
            message:
              "The game IDs you shared aren't linked to the Adjust account. Please verify app access.",
          }),
        );
      } else {
        // General error handling

        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            message: data?.message || 'Unable to register user, please try again later.',
          }),
        );
      }
    }

    fetchApplicationDetails();
  };

  const DATA_ACCESS_PROVIDERS: ProviderConfig[] = [
    {
      label: 'Appsflyer',
      header: 'Appsflyer Details',
      labelComponent: (
        <DataAccessCardHeader
          showLearnHow={false}
          provider={DataAccessProvider.APPS_FLYER}
          handleGrantAccessOpen={handleGrantAccessOpen}
        />
      ),
      value: DataAccessProvider.APPS_FLYER,
      info: (
        <AppsFlyerInformation
          accessEmail={applicationDetailsResponse?.accessEmail}
          handleScrollTo={handleScrollTo}
        />
      ),
      processingDataAccessStatusComponent: <ProcessingDataAccess />,
      fields: [
        {
          type: 'text',
          name: 'accountName',
          label: 'Account Name',
          required: true,
        },
        {
          type: 'select',
          name: 'iapMargin',
          label: 'IAP Margin',
          options: mmpConfig?.APPS_FLYER.iap || [],
          required: true,
        },
      ],
    },
    {
      label: 'Adjust',
      header: 'Adjust Details',
      labelComponent: (
        <DataAccessCardHeader
          showLearnHow={false}
          provider={DataAccessProvider.ADJUST}
          handleGrantAccessOpen={handleGrantAccessOpen}
        />
      ),
      value: DataAccessProvider.ADJUST,
      info: (
        <AdjustInformation
          accessEmail={applicationDetailsResponse?.accessEmail}
          handleScrollTo={handleScrollTo}
        />
      ),
      processingDataAccessStatusComponent: <ProcessingDataAccess />,

      fields: [
        {
          type: 'text',
          name: 'accountName',
          label: 'Account Name',
          required: true,
        },
        {
          type: 'select',
          name: 'iapMargin',
          label: 'IAP Margin',
          options: mmpConfig?.ADJUST.iap || [],
          required: true,
        },
        {
          type: 'multi-select',
          name: 'iaaIdList',
          label: 'Ad Revenue Sources',
          options: mmpConfig?.ADJUST.iaa || [],
          required: true,
        },
      ],
    },
    {
      label: 'BigQuery',
      header: 'BigQuery Details',
      labelComponent: (
        <DataAccessCardHeader
          showLearnHow={false}
          provider={DataAccessProvider.BIGQUERY}
          handleGrantAccessOpen={handleGrantAccessOpen}
        />
      ),
      value: DataAccessProvider.BIGQUERY,
      info: <></>,
      processingDataAccessStatusComponent: <ProcessingDataAccess />,
    },
    {
      label: 'GCS',
      header: 'GCS Bucket Details',
      labelComponent: (
        <DataAccessCardHeader
          showLearnHow={false}
          provider={DataAccessProvider.GCS}
          handleGrantAccessOpen={handleGrantAccessOpen}
        />
      ),
      value: DataAccessProvider.GCS,
      info: <></>,
      processingDataAccessStatusComponent: <ProcessingDataAccess />,
    },
    {
      label: 'S3',
      header: 'S3 Bucket Access',
      labelComponent: (
        <DataAccessCardHeader
          showLearnHow={false}
          provider={DataAccessProvider.S3}
          handleGrantAccessOpen={handleGrantAccessOpen}
        />
      ),
      value: DataAccessProvider.S3,
      info: <></>,
      processingDataAccessStatusComponent: <ProcessingDataAccess />,
    },
    {
      label: 'MySQL',
      header: 'MySQL Access',
      labelComponent: (
        <DataAccessCardHeader
          showLearnHow={false}
          provider={DataAccessProvider.MYSQL}
          handleGrantAccessOpen={handleGrantAccessOpen}
        />
      ),
      value: DataAccessProvider.MYSQL,
      info: <></>,
      processingDataAccessStatusComponent: <ProcessingDataAccess />,
    },
    {
      label: 'Snowflake',
      header: 'Snowflake Access',
      labelComponent: (
        <DataAccessCardHeader
          showLearnHow={false}
          provider={DataAccessProvider.SNOWFLAKE}
          handleGrantAccessOpen={handleGrantAccessOpen}
        />
      ),
      value: DataAccessProvider.SNOWFLAKE,
      info: <></>,
      processingDataAccessStatusComponent: <ProcessingDataAccess />,
    },
    {
      label: 'GoogleDrive',
      header: 'Cloud Storage Link',
      labelComponent: (
        <DataAccessCardHeader
          showLearnHow={false}
          provider={DataAccessProvider.GOOGLE_DRIVE}
          handleGrantAccessOpen={handleGrantAccessOpen}
        />
      ),
      value: DataAccessProvider.GOOGLE_DRIVE,
      info: <></>,
      processingDataAccessStatusComponent: <ProcessingDataAccess />,
    },
    {
      label: 'AggregatedCohortData',
      header: 'Cohort Performance Data',
      labelComponent: (
        <DataAccessCardHeader
          showLearnHow={false}
          provider={DataAccessProvider.AGGREGATED_DATA}
          handleGrantAccessOpen={handleGrantAccessOpen}
        />
      ),
      value: DataAccessProvider.AGGREGATED_DATA,
      info: <></>,
      processingDataAccessStatusComponent: <ProcessingDataAccess />,
    },
  ];

  return (
    <Box>
      <Stack spacing={'1rem'}>
        {/* Your App Section */}
        <>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            gap={'1rem'}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            {playStoreApp && (
              <GameDetails
                showEditIcon={false}
                setIsEditGameOpen={() => {
                  handleEditGameOpen();
                }}
                category={applicationDetailsResponse?.category}
                genre={applicationDetailsResponse?.genre}
                gameDetails={playStoreApp}
              />
            )}
            {appStoreApp && (
              <GameDetails
                showEditIcon={false}
                setIsEditGameOpen={() => {
                  handleEditGameOpen();
                }}
                category={applicationDetailsResponse?.category}
                genre={applicationDetailsResponse?.genre}
                gameDetails={appStoreApp}
              />
            )}
          </Stack>

          {!(
            applicationDetailsResponse?.category !== null &&
            applicationDetailsResponse?.genre !== null
          ) && (
            <>
              <Stack direction={'row'} gap={'1rem'}>
                <FormControl fullWidth>
                  <InputLabel id="category-label">Category</InputLabel>

                  <Select
                    value={category}
                    label="Category"
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {categoryOptions.map((category, index) => (
                      <MenuItem key={index} value={category.name}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="genre-label">Genre</InputLabel>

                  <Select
                    disabled={genreOptions.length === 0}
                    value={genre}
                    label="Genre"
                    onChange={(e) => setGenre(e.target.value)}
                  >
                    {genreOptions.map((g, index) => (
                      <MenuItem key={index} value={g}>
                        {g}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Box>
                <Button
                  disabled={!!!genre}
                  onClick={handleSubmitCategoryAndGenre}
                  variant="contained"
                  disableElevation
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                  sx={{
                    borderRadius: '100px',
                    fontSize: '1rem',
                    lineHeight: '1.25rem',
                    padding: '0.625rem 1.5rem',
                    textTransform: 'none',
                    fontWeight: 700,
                    background: '#2F736E',
                    color: '#fff',
                  }}
                >
                  Submit
                </Button>
              </Box>
            </>
          )}
        </>

        {/* Data Source Selection Sections */}
        <CustomAccordion
          open={!!accordionConfig && accordionConfig[0].open && !isOpenGrantAccessPanel}
          disableExpand={!accordionConfig?.[0].isSuccess}
          title="A. Share access to your app data with us:"
          showEdit={isOpenGrantAccessPanel}
          onEdit={() => {
            setOpenGrantAccessPanel(false);
          }}
          provider={selectedProvider}
          showProvider={isOpenGrantAccessPanel}
          borderStyle={'2px solid #2F736E1F'}
        >
          {pendingDataAccessStatus.has(applicationDetailsResponse?.requestStatus || '') && (
            <>
              {/* Automated Flow Data Sources */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  my: '1rem',
                }}
              >
                <Typography
                  fontSize={'1rem'}
                  lineHeight={'1rem'}
                  fontWeight={700}
                  color={'#6C6C6C'}
                >
                  Share Cohort Performance data via
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {[
                  DataAccessProvider.APPS_FLYER,
                  DataAccessProvider.ADJUST,
                  DataAccessProvider.AGGREGATED_DATA,
                ].map((provider) => (
                  <Grid item xs={12} sm={4}>
                    <Box
                      key={provider}
                      sx={{
                        padding: '1rem 0.75rem',
                        background: provider === selectedProvider ? '#2F736E14' : '#FFFFFF',
                        border:
                          provider === selectedProvider
                            ? '1px solid #2F736E1F'
                            : '1px solid #2F736E1F',
                        borderRadius: '0.5rem',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '0.5rem',
                        width: '100%',
                      }}
                    >
                      <Radio
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        checked={selectedProvider === provider}
                        value={provider}
                        id={`provider-${provider}`}
                        name="dataAccessProvider"
                        onChange={() => setSelectedProvider(provider)}
                      />
                      <Stack width={'100%'}>
                        <DataAccessCardHeader
                          showLearnHow={false}
                          provider={provider}
                          handleGrantAccessOpen={handleGrantAccessOpen}
                        />
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Manual Flow - Learn How */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  my: '1rem',
                }}
              >
                <Typography
                  fontSize={'1rem'}
                  lineHeight={'1rem'}
                  fontWeight={700}
                  color={'#6C6C6C'}
                  py={'1rem'}
                >
                  Share Transaction level data via
                </Typography>
                <Typography
                  fontSize={'1rem'}
                  lineHeight={'1rem'}
                  fontWeight={500}
                  color={'#6C6C6C'}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  Download our
                  <DataRequirementDocumentChip
                    sx={{
                      backgroundColor: '#FFFFFF00',
                      border: 'none',
                      color: '#51B8B0',
                      fontSize: '0.95rem',
                      px: '0.3rem',
                      '&:hover': { opacity: 0.7 },
                    }}
                  />
                  for guidance on how to prepare your data
                </Typography>
              </Box>

              {/* Manual Flow Data Sources */}
              <Grid container spacing={2}>
                {[
                  DataAccessProvider.BIGQUERY,
                  DataAccessProvider.GCS,
                  DataAccessProvider.S3,
                  DataAccessProvider.MYSQL,
                  DataAccessProvider.SNOWFLAKE,
                  DataAccessProvider.GOOGLE_DRIVE,
                ].map((provider) => (
                  <Grid item xs={12} sm={4}>
                    <Box
                      key={provider}
                      sx={{
                        padding: '1rem 0.75rem',
                        background: provider === selectedProvider ? '#2F736E14' : '#FFFFFF',
                        border:
                          provider === selectedProvider
                            ? '1px solid #2F736E1F'
                            : '1px solid #2F736E1F',
                        borderRadius: '0.5rem',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '0.5rem',
                        alignItems: 'center',
                      }}
                    >
                      <Radio
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        checked={selectedProvider === provider}
                        value={provider}
                        id={`provider-${provider}`}
                        name="dataAccessProvider"
                        onChange={() => setSelectedProvider(provider)}
                      />
                      <Stack width={'100%'}>
                        <DataAccessCardHeader
                          showLearnHow={false}
                          provider={provider}
                          handleGrantAccessOpen={handleGrantAccessOpen}
                        />
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {processingDataAccessStatus.has(applicationDetailsResponse?.requestStatus || '') &&
            DATA_ACCESS_PROVIDERS.filter(
              (provider) => provider.value === applicationDetailsResponse?.mmp,
            ).map((provider) => (
              <Box
                key={'access-granted-data-processing-stage'}
                sx={{
                  padding: '1rem 0.75rem',
                  background: provider.value === selectedProvider ? '#2F736E14' : '#F0F0F1',
                  border:
                    provider.value === selectedProvider
                      ? '1px solid #7FBCAD'
                      : '1px solid #2F736E1F',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '0.5rem',
                  alignItems: 'flex-start',
                }}
              >
                <Radio
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                  checked={selectedProvider === provider.value}
                  value={provider.value}
                  id={`provider-${provider.value}`}
                  name="dataAccessProvider"
                  onChange={() => setSelectedProvider(provider.value)}
                />
                <Stack width={'100%'}>
                  {/* Processing State Label */}
                  {provider.labelComponent ? (
                    provider.labelComponent
                  ) : (
                    <Typography
                      fontSize={'0.75rem'}
                      lineHeight={'1rem'}
                      fontWeight={500}
                      color={'#6C6C6C'}
                    >
                      {provider.label}
                    </Typography>
                  )}

                  {/* Processing State Card */}
                  {provider.value === selectedProvider &&
                    processingDataAccessStatus.has(
                      applicationDetailsResponse?.requestStatus || '',
                    ) &&
                    provider.processingDataAccessStatusComponent &&
                    provider.processingDataAccessStatusComponent}
                </Stack>
              </Box>
            ))}

          {/* Note Section and Continue Button */}
          {pendingDataAccessStatus.has(applicationDetailsResponse?.requestStatus || '') && (
            <Box>
              <Box mb={'1rem'} mt={'1rem'}>
                <Typography
                  component={'span'}
                  fontSize={'0.875rem'}
                  lineHeight={'1.25rem'}
                  fontWeight={700}
                  color={'#6C6C6C'}
                >
                  Note:
                </Typography>
                <ol
                  style={{
                    paddingLeft: '1rem',
                    margin: 0,
                  }}
                >
                  <Typography
                    component={'li'}
                    fontSize={'0.875rem'}
                    lineHeight={'1.25rem'}
                    fontWeight={500}
                    color={'#6C6C6C'}
                  >
                    We do not collect any PII data such as device id, GAID or IDFA for the purpose
                    of our analysis.
                  </Typography>

                  <Typography
                    component={'li'}
                    fontSize={'0.875rem'}
                    lineHeight={'1.25rem'}
                    fontWeight={500}
                    color={'#6C6C6C'}
                  >
                    This data is used for the purpose of evaluating the cohorts’ and to prepare a
                    completely anonymised data set for the purposes of creating benchmarks.
                  </Typography>

                  <Typography
                    component={'li'}
                    fontSize={'0.875rem'}
                    lineHeight={'1.25rem'}
                    fontWeight={500}
                    color={'#6C6C6C'}
                  >
                    The data you provide may be used to train our internal models in an anonymized
                    and aggregated manner to improve our services.{' '}
                  </Typography>

                  <Typography
                    component={'li'}
                    fontSize={'0.875rem'}
                    lineHeight={'1.25rem'}
                    fontWeight={500}
                    color={'#6C6C6C'}
                  >
                    Sharing only <span style={{ color: '#000000' }}>Aggregated Cohort Data</span>{' '}
                    supports only Underwriting Analysis and Funding Eligibility Assessment.
                    Financial Projections and Benchmarks will be unavailable{' '}
                  </Typography>
                </ol>
              </Box>
              <Button
                disableElevation
                disableFocusRipple
                disableRipple
                disableTouchRipple
                variant="contained"
                onClick={handleGrantAccessContinue}
                sx={{
                  borderRadius: '100px',
                  fontSize: '14px',
                  lineHeight: '1.25rem',
                  padding: '0.625rem 1.5rem',
                  textTransform: 'none',
                  fontWeight: 700,
                  background: '#2F736E',
                  color: '#fff',
                }}
              >
                Continue
              </Button>
            </Box>
          )}
        </CustomAccordion>

        {/* Data Source Access Sidepanel */}
        <CustomAccordion
          title="B. Data integration details"
          open={isOpenGrantAccessPanel}
          disableExpand={!isOpenGrantAccessPanel}
          borderStyle={'2px solid #2F736E1F'}
        >
          <GrantAccessToProvider
            scrollTo={accessGrantScrollTo}
            loading={isDataAccessUpdating}
            onSaveAccess={handleGrantAccessSave}
            config={activeConfig}
            selfServeId={applicationDetailsResponse?.selfServeId as string}
            monthlyRefreshData={{
              gameId: data.gameId,
              monthlyRefreshStatus: data.monthlyRefreshStatus || false,
            }}
            fetchApplicationDetails={fetchApplicationDetails}
            refreshStatus={refreshStatus}
            setRefreshStatus={setRefreshStatus}
          />
        </CustomAccordion>

        {errorDataAccessStatus.has(applicationDetailsResponse?.requestStatus || '') && (
          <DataProcessingErrorCard
            dataSource={applicationDetailsResponse?.mmp as DataAccessProvider}
            status={applicationDetailsResponse?.requestStatus as SelfServeDataAccessStatus}
          />
        )}
      </Stack>

      {/* Edit Game Sidepanel */}
      {isEditGameOpen && (
        <DrawerComponent
          anchor="right"
          open={isEditGameOpen}
          close={(status: string) => setIsEditGameOpen(false)}
          height="98vh"
          zIndex={1203}
          backgroundColor="#F0F0F1"
        >
          <EditGame
            close={handleEditGameClose}
            selfServeId={applicationDetailsResponse?.selfServeId as string}
            playStoreApp={playStoreApp}
            appStoreApp={appStoreApp}
          />
        </DrawerComponent>
      )}
    </Box>
  );
};

export default PendingAccessCard;
